import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  IconButton,
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Alert
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { 
  updateEventPriority,
  extractCommentHistory,
  parseExistingRemarks,
  extractClosureInfo
} from '../../utils/EventService';
import { generateSingleEventPDF } from './SingleEventPDFGenerator';

const updateEventPriorityByEventId = updateEventPriority;

const PRIORITY_LEVELS = [
  { value: 'High', color: '#f44336' },
  { value: 'Moderate', color: '#ff9800' },
  { value: 'Low', color: '#4caf50' }
];

// Helper functions for event closure management
const EVENT_CLOSURE_MARKER = 'EVENT_CLOSED_BY_USER:';
const EVENT_REOPEN_MARKER = 'EVENT_REOPENED_BY_USER:';

const isEventClosed = (remarks) => {
  // Parse remarks to get comment history
  const parsedComments = parseExistingRemarks(remarks);
  
  if (!Array.isArray(parsedComments) || parsedComments.length === 0) return false;

  // Find the most recent closure/reopen action
  const closureRemarks = parsedComments.filter(remark =>
    remark.comment && (
      remark.comment.startsWith(EVENT_CLOSURE_MARKER) ||
      remark.comment.startsWith(EVENT_REOPEN_MARKER)
    )
  );

  if (closureRemarks.length === 0) return false;

  // Get the most recent action by timestamp
  const sortedActions = closureRemarks.sort((a, b) => 
    new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
  );
  
  const lastAction = sortedActions[0];
  return lastAction.comment.startsWith(EVENT_CLOSURE_MARKER);
};

const getEventClosureInfo = (remarks) => {
  return extractClosureInfo(remarks);
};

// Helper function to filter events by status
export const filterEventsByStatus = (events, status = 'all') => {
  if (!Array.isArray(events)) return [];

  return events.filter(event => {
    const isClosed = isEventClosed(event.remarks);

    switch (status) {
      case 'closed':
        return isClosed;
      case 'open':
        return !isClosed;
      case 'all':
      default:
        return true;
    }
  });
};

// Helper function to get event statistics
export const getEventStatistics = (events) => {
  if (!Array.isArray(events)) return { total: 0, open: 0, closed: 0 };

  const total = events.length;
  const closed = events.filter(event => isEventClosed(event.remarks)).length;
  const open = total - closed;

  return { total, open, closed };
};

// Enhanced function to check if event has been updated (priority OR user comments)
const hasUserUpdates = (eventDetails) => {
  if (!eventDetails) return false;

  // Check if it has priority/severity
  if (eventDetails.severity && eventDetails.severity !== 'normal') return true;

  // Check if it has user comments (using enhanced comment extraction)
  const userComments = extractCommentHistory(eventDetails.remarks);
  return userComments.length > 0;
};

const ImageModal = ({
  isOpen,
  onClose,
  imageSource,
  area,
  location,
  event = null,
  onEventUpdated,
  updateEventPriority: updateLivePriority,
  isReportView = false
}) => {
  const [priority, setPriority] = useState('');
  const [comment, setComment] = useState('');
  const [user, setUser] = useState('admin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // PDF report generation states
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportProgress, setReportProgress] = useState(null);

  // Event closure states
  const [isClosingEvent, setIsClosingEvent] = useState(false);
  const [isReopeningEvent, setIsReopeningEvent] = useState(false);

  useEffect(() => {
    if (isOpen && event) {
      // Set priority from event severity
      setPriority(event.severity || '');

      // Parse the event remarks using enhanced parsing
      const parsedComments = parseExistingRemarks(event.remarks);
      const commentHistory = extractCommentHistory(event.remarks);
      const closureInfo = extractClosureInfo(event.remarks);

      setEventDetails({
        ...event,
        severity: event.severity || null,
        remarks: parsedComments,
        commentHistory: commentHistory,
        closureInfo: closureInfo
      });

      // Reset form states
      setComment('');
      setUser('admin');
      setError(null);
      setSuccess(false);
      setIsSubmitting(false);
      setIsGeneratingReport(false);
      setReportProgress(null);
      setIsClosingEvent(false);
      setIsReopeningEvent(false);

      console.log('Enhanced event details set:', {
        severity: event.severity,
        totalComments: parsedComments.length,
        userComments: commentHistory.length,
        closureInfo: closureInfo,
        eventId: event.eventId || event.id,
        isReportView,
        isClosed: isEventClosed(event.remarks),
        hasUserUpdates: hasUserUpdates(event)
      });
    } else if (!isOpen) {
      // Reset all states when modal is closed
      setPriority('');
      setComment('');
      setUser('admin');
      setError(null);
      setSuccess(false);
      setIsSubmitting(false);
      setEventDetails(null);
      setIsDownloading(false);
      setIsGeneratingReport(false);
      setReportProgress(null);
      setIsClosingEvent(false);
      setIsReopeningEvent(false);
    }
  }, [isOpen, event, isReportView]);

  // Check if event is currently closed
  const currentEventClosed = eventDetails ? isEventClosed(eventDetails.remarks) : false;
  const closureInfo = eventDetails ? eventDetails.closureInfo : null;

  // Check if event has been updated before (has priority OR user comments)
  const hasBeenUpdated = hasUserUpdates(eventDetails);

  // Handle image download (existing function remains same)
  const handleDownloadImage = async () => {
    if (!imageSource) return;

    setIsDownloading(true);

    try {
      const eventId = event?.eventId || event?.id || 'unknown';
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const areaName = area?.replace(/[^a-zA-Z0-9]/g, '_') || 'unknown';
      const locationName = location?.replace(/[^a-zA-Z0-9]/g, '_') || 'unknown';

      const filename = `Event_${eventId}_${areaName}_${locationName}_${timestamp}.jpg`;

      const response = await fetch(imageSource);
      if (!response.ok) throw new Error('Failed to fetch image');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Failed to download image:', error);
      setError('Failed to download image: ' + error.message);

      setTimeout(() => {
        if (error && error.includes('Failed to download image')) {
          setError(null);
        }
      }, 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle single event PDF report generation (existing function remains same)
  const handleGenerateEventReport = async () => {
    if (!event || !area || !location) {
      setError('Missing event data required for report generation');
      return;
    }

    setIsGeneratingReport(true);
    setReportProgress({ stage: 'starting', percentage: 0, message: 'Initializing...' });
    setError(null);

    try {
      const reportEvent = eventDetails || event;

      const result = await generateSingleEventPDF(
        reportEvent,
        area,
        location,
        (progress) => {
          setReportProgress(progress);
        }
      );

      if (result.success) {
        setReportProgress({
          stage: 'complete',
          percentage: 100,
          message: `Report downloaded: ${result.filename}`
        });

        setTimeout(() => {
          setReportProgress(null);
        }, 3000);
      }

    } catch (error) {
      console.error('Failed to generate event report:', error);
      setError('Failed to generate report: ' + error.message);
      setReportProgress(null);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Enhanced event closure handler
  const handleCloseEvent = async () => {
    if (!event || !priority) {
      setError('Please set priority before closing the event');
      return;
    }

    setIsClosingEvent(true);
    setError(null);
    setSuccess(false);

    try {
      const closureData = {
        closedBy: user,
        closedAt: new Date().toISOString(),
        priority: priority,
        finalComment: comment || 'Event closed without additional comments'
      };

      const closureRemark = {
        user,
        comment: EVENT_CLOSURE_MARKER + JSON.stringify(closureData),
        timestamp: new Date().toISOString()
      };

      const baseUrl = event.httpsUrl;

      if (baseUrl) {
        try {
          const eventId = event.eventId || event.id;

          if (eventId) {
            // Pass frontend comments as fallback for closure
            const frontendComments = eventDetails?.remarks || eventDetails?.commentHistory || [];
            console.log(`🔧 Passing ${frontendComments.length} frontend comments as fallback for closure`);

            // Use enhanced updateEventPriority that preserves comment history
            const updateResponse = await updateEventPriorityByEventId(
              baseUrl,
              eventId,
              priority,
              closureRemark,
              frontendComments // Pass current frontend comments
            );

            console.log('Event closure database update response:', updateResponse);
          } else {
            console.warn('No eventId found for closure update');
          }
        } catch (dbError) {
          console.error('Event closure database update failed:', dbError);
          setError('Failed to close event in database: ' + dbError.message);
          return;
        }
      } else {
        console.warn('No httpsUrl found - skipping database update');
        setError('Cannot close event - missing API connection');
        return;
      }

      // Update live dashboard state
      if (!isReportView && updateLivePriority) {
        const eventId = event.eventId || event.id;
        if (eventId && priority) {
          // Add closure remark to existing comments array
          const updatedComments = [...(eventDetails?.remarks || []), closureRemark];
          updateLivePriority(eventId, priority, updatedComments);
        }
      }

      // Update local state with preserved comments
      const updatedComments = [...(eventDetails?.remarks || []), closureRemark];
      const updatedCommentHistory = extractCommentHistory(updatedComments);
      const updatedClosureInfo = extractClosureInfo(updatedComments);

      const updatedEventDetails = {
        ...eventDetails,
        severity: priority,
        remarks: updatedComments,
        commentHistory: updatedCommentHistory,
        closureInfo: updatedClosureInfo,
        priorityUpdated: true,
        priorityUpdatedAt: Date.now(),
        eventClosed: true,
        closedAt: new Date().toISOString()
      };

      setEventDetails(updatedEventDetails);

      if (onEventUpdated) {
        onEventUpdated(updatedEventDetails);
      }

      setSuccess(true);
      setComment('');

      setTimeout(() => {
        setSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Failed to close event:', err);
      setError('Failed to close event: ' + err.message);
    } finally {
      setIsClosingEvent(false);
    }
  };

  // Enhanced event reopening handler
  const handleReopenEvent = async () => {
    if (!event) {
      setError('No event data available');
      return;
    }

    setIsReopeningEvent(true);
    setError(null);
    setSuccess(false);

    try {
      const reopenRemark = {
        user,
        comment: EVENT_REOPEN_MARKER + `Event reopened by ${user}`,
        timestamp: new Date().toISOString()
      };

      const baseUrl = event.httpsUrl;

      if (baseUrl) {
        try {
          const eventId = event.eventId || event.id;

          if (eventId) {
            // Pass frontend comments as fallback for reopen
            const frontendComments = eventDetails?.remarks || eventDetails?.commentHistory || [];
            console.log(`🔧 Passing ${frontendComments.length} frontend comments as fallback for reopen`);

            // Use enhanced updateEventPriority that preserves comment history
            const updateResponse = await updateEventPriorityByEventId(
              baseUrl,
              eventId,
              null, // Don't change priority when reopening
              reopenRemark,
              frontendComments // Pass current frontend comments
            );

            console.log('Event reopen database update response:', updateResponse);
          }
        } catch (dbError) {
          console.error('Event reopen database update failed:', dbError);
          setError('Failed to reopen event in database: ' + dbError.message);
          return;
        }
      }

      // Update live dashboard state
      if (!isReportView && updateLivePriority) {
        const eventId = event.eventId || event.id;
        if (eventId) {
          const updatedComments = [...(eventDetails?.remarks || []), reopenRemark];
          updateLivePriority(eventId, eventDetails?.severity || priority, updatedComments);
        }
      }

      // Update local state with preserved comments
      const updatedComments = [...(eventDetails?.remarks || []), reopenRemark];
      const updatedCommentHistory = extractCommentHistory(updatedComments);
      const updatedClosureInfo = extractClosureInfo(updatedComments);

      const updatedEventDetails = {
        ...eventDetails,
        remarks: updatedComments,
        commentHistory: updatedCommentHistory,
        closureInfo: updatedClosureInfo,
        eventClosed: false
      };

      setEventDetails(updatedEventDetails);

      if (onEventUpdated) {
        onEventUpdated(updatedEventDetails);
      }

      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Failed to reopen event:', err);
      setError('Failed to reopen event: ' + err.message);
    } finally {
      setIsReopeningEvent(false);
    }
  };

  // Enhanced regular form submission with comment preservation
  const handleSubmit = async () => {
    if (!priority && !comment) return;
    if (currentEventClosed) return; // Prevent updates when closed

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Check if we have the required event data
      if (!event) {
        console.error('No event data provided');
        setError('No event data available for update');
        return;
      }

      const remarks = comment ? {
        user,
        comment,
        timestamp: new Date().toISOString()
      } : undefined;

      const baseUrl = event.httpsUrl;
      let databaseUpdateSuccess = false;

      if (baseUrl) {
        try {
          const eventId = event.eventId || event.id;

          if (eventId) {
            // Pass frontend comments as fallback
            const frontendComments = eventDetails?.remarks || eventDetails?.commentHistory || [];
            console.log(`🔧 Passing ${frontendComments.length} frontend comments as fallback`);

            // Use enhanced updateEventPriority with frontend fallback
            const updateResponse = await updateEventPriorityByEventId(
              baseUrl,
              eventId,
              priority,
              remarks,
              frontendComments // Pass current frontend comments
            );

            console.log('Enhanced database update response:', updateResponse);
            databaseUpdateSuccess = true;
            
            // If the response contains parsed comments, use them
            if (updateResponse.parsedComments) {
              console.log('✅ Received parsed comments from API:', updateResponse.parsedComments.length);
            }
          } else {
            console.warn('No eventId found for database update');
          }
        } catch (dbError) {
          console.error('Database update failed:', dbError);
          setError('Database update failed: ' + dbError.message + ' (Live update will still work)');
        }
      } else {
        console.warn('No httpsUrl found - skipping database update');
      }

      // Update live dashboard state (for both live and report views)
      if (!isReportView && updateLivePriority) {
        const eventId = event.eventId || event.id;
        if (eventId && priority) {
          // Add new remark to existing comments array
          const existingComments = eventDetails?.remarks || [];
          const newComments = remarks ? [...existingComments, remarks] : existingComments;
          updateLivePriority(eventId, priority, newComments);
        }
      }

      // Update local state with preserved comments
      const updatedComments = remarks
        ? [...(eventDetails?.remarks || []), remarks]
        : eventDetails?.remarks || [];

      const updatedCommentHistory = extractCommentHistory(updatedComments);
      const updatedClosureInfo = extractClosureInfo(updatedComments);

      const updatedEventDetails = {
        ...eventDetails,
        severity: priority,
        remarks: updatedComments,
        commentHistory: updatedCommentHistory,
        closureInfo: updatedClosureInfo,
        priorityUpdated: true,
        priorityUpdatedAt: Date.now()
      };

      setEventDetails(updatedEventDetails);

      if (onEventUpdated) {
        onEventUpdated(updatedEventDetails);
      }

      if (databaseUpdateSuccess) {
        setSuccess(true);
      } else if (!isReportView) {
        setSuccess(true);
      }

      setComment('');

      setTimeout(() => {
        setSuccess(false);
        if (error && error.includes('Live update will still work')) {
          setError(null);
        }
      }, 3000);

    } catch (err) {
      console.error('Failed to update event:', err);
      setError('Failed to update event: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Derived accent colour based on current priority (or teal default)
  const accentColor = eventDetails?.severity
    ? (PRIORITY_LEVELS.find(p => p.value === eventDetails.severity)?.color || '#1ABC9C')
    : '#1ABC9C';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: 'white',
      fontSize: '0.82rem',
      bgcolor: 'rgba(255,255,255,0.04)',
      borderRadius: '10px',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
      '&.Mui-focused fieldset': { borderColor: accentColor },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: accentColor },
    '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
    '& textarea::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a2332',
          color: 'white',
          height: '88vh',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '14px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        }
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 1.5,
        background: 'linear-gradient(135deg, #1a2332 0%, #243447 100%)',
        borderBottom: `2px solid ${accentColor}`,
        flexShrink: 0,
      }}>
        {/* Left: area + location + status chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: `${accentColor}20`, border: `1px solid ${accentColor}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, flexShrink: 0,
          }}>
            {currentEventClosed ? '🔒' : '📷'}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2, fontSize: '0.95rem' }}>
                {area?.toUpperCase()} · {location}
              </Typography>

              {/* Priority chip */}
              {eventDetails?.severity && (
                <Chip label={eventDetails.severity} size="small" sx={{
                  bgcolor: accentColor, color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 20
                }} />
              )}

              {/* Closed chip */}
              {currentEventClosed && (
                <Chip icon={<LockIcon sx={{ fontSize: '0.75rem !important' }} />} label="CLOSED" size="small" sx={{
                  bgcolor: 'rgba(244,67,54,0.2)', color: '#f44336',
                  border: '1px solid rgba(244,67,54,0.4)', fontWeight: 700, fontSize: '0.65rem', height: 20
                }} />
              )}

              {/* Saved chip */}
              {!currentEventClosed && eventDetails?.priorityUpdated && (
                <Chip label={isReportView ? 'DB Saved' : 'Live + DB'} size="small" sx={{
                  bgcolor: 'rgba(26,188,156,0.2)', color: '#1ABC9C',
                  border: '1px solid rgba(26,188,156,0.35)', fontWeight: 700, fontSize: '0.65rem', height: 20
                }} />
              )}

              {/* Comment count */}
              {eventDetails?.commentHistory?.length > 0 && (
                <Chip label={`${eventDetails.commentHistory.length} comment${eventDetails.commentHistory.length > 1 ? 's' : ''}`} size="small" sx={{
                  bgcolor: 'rgba(33,150,243,0.15)', color: '#64b5f6',
                  border: '1px solid rgba(33,150,243,0.25)', fontSize: '0.65rem', height: 20
                }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}>
              {isReportView ? 'Report Mode' : 'Live + DB Mode'}
            </Typography>
          </Box>
        </Box>

        {/* Right: close button */}
        <IconButton onClick={onClose} size="small" sx={{
          color: 'rgba(255,255,255,0.6)', borderRadius: '8px',
          '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' }
        }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* LEFT — image */}
        <Box sx={{
          flex: '1 1 60%', position: 'relative', bgcolor: '#0d1520',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {imageSource ? (
            <>
              <img
                src={imageSource}
                alt="Event Visual"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              {/* Floating action icons */}
              <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 1 }}>
                <Tooltip title="Download Event Report (PDF)">
                  <IconButton onClick={handleGenerateEventReport} disabled={!event || isGeneratingReport} size="small" sx={{
                    bgcolor: 'rgba(156,39,176,0.82)', color: 'white', borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(156,39,176,0.95)' },
                    '&:disabled': { bgcolor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.3)' }
                  }}>
                    {isGeneratingReport ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DescriptionIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Image">
                  <IconButton onClick={handleDownloadImage} disabled={isDownloading} size="small" sx={{
                    bgcolor: 'rgba(33,150,243,0.82)', color: 'white', borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(33,150,243,0.95)' },
                    '&:disabled': { bgcolor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.3)' }
                  }}>
                    {isDownloading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* PDF progress overlay */}
              {reportProgress && (
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  p: 1.5, bgcolor: 'rgba(15,20,30,0.88)',
                  borderTop: '1px solid rgba(156,39,176,0.3)',
                  backdropFilter: 'blur(4px)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <DescriptionIcon sx={{ color: '#9c27b0', fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ color: '#ce93d8', fontSize: '0.72rem' }}>
                      {reportProgress.message}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9c27b0', ml: 'auto', fontSize: '0.72rem' }}>
                      {reportProgress.percentage}%
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 4, height: 3 }}>
                    <Box sx={{
                      width: `${reportProgress.percentage}%`, bgcolor: '#9c27b0',
                      borderRadius: 4, height: '100%', transition: 'width 0.3s ease'
                    }} />
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress sx={{ color: '#1ABC9C' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}>
                Loading image…
              </Typography>
            </Box>
          )}
        </Box>

        {/* RIGHT — controls + history */}
        <Box sx={{
          width: 360, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          bgcolor: '#1a2332',
          borderLeft: '1px solid rgba(255,255,255,0.07)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
        }}>

          {/* ── Closure info banner ───────────────────────────────────────── */}
          {currentEventClosed && closureInfo && (
            <Box sx={{
              mx: 2, mt: 2, p: 1.5, borderRadius: 2,
              bgcolor: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.25)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <LockIcon sx={{ color: '#f44336', fontSize: '0.85rem' }} />
                <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Event Closed
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', lineHeight: 1.6, display: 'block' }}>
                By: <strong style={{ color: 'white' }}>{closureInfo.closedBy}</strong><br />
                At: {closureInfo.formattedClosedAt}<br />
                Priority: <strong style={{ color: accentColor }}>{closureInfo.priority}</strong>
                {closureInfo.finalComment && closureInfo.finalComment !== 'Event closed without additional comments' && (
                  <><br />Note: "{closureInfo.finalComment}"</>
                )}
              </Typography>
            </Box>
          )}

          {/* ── Update / Reopen panel ─────────────────────────────────────── */}
          <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography variant="caption" sx={{
              color: currentEventClosed ? '#f44336' : accentColor,
              fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em'
            }}>
              {currentEventClosed ? 'Event Closed' : 'Update Event'}
            </Typography>

            {!currentEventClosed ? (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

                {/* Priority selector — inline colour pills */}
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem', mb: 0.75, display: 'block' }}>
                    Priority
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {PRIORITY_LEVELS.map(lvl => (
                      <Box
                        key={lvl.value}
                        onClick={() => setPriority(lvl.value)}
                        sx={{
                          flex: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer',
                          textAlign: 'center', fontSize: '0.72rem', fontWeight: 700,
                          border: `1px solid ${priority === lvl.value ? lvl.color : 'rgba(255,255,255,0.1)'}`,
                          bgcolor: priority === lvl.value ? `${lvl.color}22` : 'rgba(255,255,255,0.03)',
                          color: priority === lvl.value ? lvl.color : 'rgba(255,255,255,0.45)',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: `${lvl.color}18`, borderColor: lvl.color, color: lvl.color }
                        }}
                      >
                        {lvl.value}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Name */}
                <TextField
                  label="Your Name / Designation"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  sx={inputSx}
                />

                {/* Comment */}
                <TextField
                  label="Add a comment"
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  variant="outlined"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  sx={inputSx}
                />

                {/* Feedback messages */}
                {error && (
                  <Box sx={{ p: 1, bgcolor: 'rgba(244,67,54,0.1)', borderRadius: '8px', border: '1px solid rgba(244,67,54,0.3)' }}>
                    <Typography variant="caption" sx={{ color: '#ef9a9a', fontSize: '0.72rem' }}>{error}</Typography>
                  </Box>
                )}
                {success && (
                  <Box sx={{ p: 1, bgcolor: 'rgba(76,175,80,0.1)', borderRadius: '8px', border: '1px solid rgba(76,175,80,0.3)' }}>
                    <Typography variant="caption" sx={{ color: '#a5d6a7', fontSize: '0.72rem' }}>
                      ✓ {isClosingEvent ? 'Event closed successfully!' : 'Event updated successfully!'}
                    </Typography>
                  </Box>
                )}

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Button
                    variant="contained" fullWidth size="small"
                    endIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: '0.9rem !important' }} />}
                    onClick={handleSubmit}
                    disabled={isSubmitting || (!priority && !comment)}
                    sx={{
                      bgcolor: '#1ABC9C', borderRadius: '8px', fontWeight: 700, fontSize: '0.72rem', py: 1,
                      '&:hover': { bgcolor: '#16a085' },
                      '&:disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)' }
                    }}
                  >
                    {isSubmitting ? 'Saving…' : 'Save'}
                  </Button>

                  {hasBeenUpdated && (
                    <Button
                      variant="contained" fullWidth size="small"
                      endIcon={isClosingEvent ? <CircularProgress size={14} color="inherit" /> : <LockIcon sx={{ fontSize: '0.9rem !important' }} />}
                      onClick={handleCloseEvent}
                      disabled={isClosingEvent || !priority}
                      sx={{
                        bgcolor: 'rgba(244,67,54,0.85)', borderRadius: '8px', fontWeight: 700, fontSize: '0.72rem', py: 1,
                        '&:hover': { bgcolor: '#d32f2f' },
                        '&:disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)' }
                      }}
                    >
                      {isClosingEvent ? 'Closing…' : 'Close'}
                    </Button>
                  )}
                </Box>

                {/* Helper text */}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '0.63rem', mt: 0.25 }}>
                  {isReportView
                    ? 'Saved to database with full comment history'
                    : 'Saved to database · shown live until page refresh'}
                </Typography>
              </Box>
            ) : (
              /* Closed state — reopen only */
              <Box sx={{ mt: 1.5 }}>
                {error && (
                  <Box sx={{ mb: 1, p: 1, bgcolor: 'rgba(244,67,54,0.1)', borderRadius: '8px', border: '1px solid rgba(244,67,54,0.3)' }}>
                    <Typography variant="caption" sx={{ color: '#ef9a9a', fontSize: '0.72rem' }}>{error}</Typography>
                  </Box>
                )}
                {success && (
                  <Box sx={{ mb: 1, p: 1, bgcolor: 'rgba(76,175,80,0.1)', borderRadius: '8px', border: '1px solid rgba(76,175,80,0.3)' }}>
                    <Typography variant="caption" sx={{ color: '#a5d6a7', fontSize: '0.72rem' }}>✓ Event reopened successfully!</Typography>
                  </Box>
                )}
                <Button
                  variant="contained" fullWidth size="small"
                  startIcon={isReopeningEvent ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={handleReopenEvent}
                  disabled={isReopeningEvent}
                  sx={{
                    bgcolor: 'rgba(76,175,80,0.85)', borderRadius: '8px', fontWeight: 700, fontSize: '0.75rem', py: 1,
                    '&:hover': { bgcolor: '#388e3c' },
                    '&:disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)' }
                  }}
                >
                  {isReopeningEvent ? 'Reopening…' : 'Reopen Event'}
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Downloads ────────────────────────────────────────────────── */}
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined" size="small" fullWidth
              startIcon={isDownloading ? <CircularProgress size={13} /> : <DownloadIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={handleDownloadImage}
              disabled={!imageSource || isDownloading}
              sx={{
                borderColor: 'rgba(33,150,243,0.4)', color: '#64b5f6', borderRadius: '8px', fontSize: '0.7rem',
                '&:hover': { borderColor: '#2196f3', bgcolor: 'rgba(33,150,243,0.08)', color: '#2196f3' },
                '&:disabled': { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.2)' }
              }}
            >
              {isDownloading ? 'Saving…' : 'Image'}
            </Button>
            <Button
              variant="outlined" size="small" fullWidth
              startIcon={isGeneratingReport ? <CircularProgress size={13} color="inherit" /> : <DescriptionIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={handleGenerateEventReport}
              disabled={!event || isGeneratingReport}
              sx={{
                borderColor: 'rgba(156,39,176,0.4)', color: '#ce93d8', borderRadius: '8px', fontSize: '0.7rem',
                '&:hover': { borderColor: '#9c27b0', bgcolor: 'rgba(156,39,176,0.08)', color: '#9c27b0' },
                '&:disabled': { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.2)' }
              }}
            >
              {isGeneratingReport ? 'Building…' : 'PDF Report'}
            </Button>
          </Box>

          {/* ── Comment history ───────────────────────────────────────────── */}
          <Box sx={{ p: 2.5, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Comment History
              </Typography>
              {eventDetails?.commentHistory?.length > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.63rem' }}>
                  {eventDetails.commentHistory.length} · latest first
                </Typography>
              )}
            </Box>

            {eventDetails?.commentHistory?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {eventDetails.commentHistory.map((c, i) => (
                  <Box key={i} sx={{
                    p: 1.25, borderRadius: '10px',
                    bgcolor: i === 0 ? 'rgba(26,188,156,0.08)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${i === 0 ? 'rgba(26,188,156,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: i === 0 ? '#1ABC9C' : 'rgba(255,255,255,0.55)', fontWeight: 700, fontSize: '0.7rem' }}>
                          {c.user}
                        </Typography>
                        {i === 0 && (
                          <Box sx={{ px: 0.6, py: 0.1, bgcolor: 'rgba(26,188,156,0.15)', borderRadius: '4px' }}>
                            <Typography variant="caption" sx={{ color: '#1ABC9C', fontSize: '0.55rem', fontWeight: 700 }}>LATEST</Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.62rem' }}>
                        {c.formattedTime}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', lineHeight: 1.5 }}>
                      {c.comment}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px dashed rgba(255,255,255,0.08)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', fontStyle: 'italic' }}>
                  No comments yet
                </Typography>
              </Box>
            )}

            {/* DB preservation indicator */}
            {eventDetails?.remarks?.length > 0 && (
              <Box sx={{ mt: 1.5, px: 1, py: 0.75, bgcolor: 'rgba(33,150,243,0.07)', borderRadius: '8px', border: '1px solid rgba(33,150,243,0.15)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(100,181,246,0.7)', fontSize: '0.63rem' }}>
                  💾 {eventDetails.remarks.length} entries preserved in DB ({eventDetails.commentHistory?.length || 0} user · rest system)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImageModal;